"use client";
import { FundComparisonResult } from "@/type/schemeType";
import { useEffect, useRef, useState } from "react";
import ArrowLinkButtonDesk from "../ui/ArrowLinkButtonDesk";
import ArrowLinkButtonMob from "../ui/ArrowLinkButtonMob";
import FundComparisonCard from "@/components/cards/FundComparisonCard";
import FundForComparisonCard from "@/components/cards/FundForComparisonCard";
import SectionHeading from "../others/SectionHeading";
import InformationButton from "@/components/ui/InformationButton";

type Props = {
  uCode: string;
};

export default function CompareFundsSection({ uCode }: Props) {
  const [compareFunds, setCompareFunds] = useState<FundComparisonResult[]>([]);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!uCode) return;

    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`/api/compareFunds?u_code=${uCode}`);

        const data = await res.json();
        if (!res.ok) throw new Error("Failed to fetch managers");

        setCompareFunds(data || []);
      } catch (err) {
        console.error("Compare funds fetch failed:", err);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [uCode]);

  const uniqueCompareFunds = Array.from(new Map(compareFunds.map((fund) => [fund.compare_id, fund])).values());

  if (!compareFunds.length) return <div ref={ref} />;

  return (
    <section className="mb-8 xs:mb-12" ref={ref}>
      <div className="flex items-center gap-3 mb-2 sm:mb-4">
        <SectionHeading heading="Fund Comparison" />
        <InformationButton keywords="fund-comparison" />

        <ArrowLinkButtonDesk href={`/mutual-fund/compare`} text="Compare Funds" />
      </div>

      <div className="flex gap-6 sm:gap-16 xl:gap-1 items-center justify-evenly xl:justify-between overflow-auto xs:py-2.5 scrollbar-none sm:scrollbar-xs">
        {uniqueCompareFunds.map((fund: FundComparisonResult, index: number) => (
          <FundComparisonCard key={fund.compare_id || index} fund={fund} />
        ))}

        <FundForComparisonCard fund={compareFunds[0]} />
      </div>

      <ArrowLinkButtonMob href={`/mutual-fund/compare`} text="Compare Funds" className="mt-5" />
    </section>
  );
}
